import type { AuthContextProps, AuthProviderProps } from "react-oidc-context";
import { queryOptions } from "@tanstack/react-query";

export interface AuthDiscovery {
  provider_url: string;
  client_id: string;
  scope: string;
  provider: "oidc" | "entra" | "cognito";
  logout_url: string | null;
}

export function parseSupportEmail(value: unknown): string | null {
  if (!value || typeof value !== "object" || !("support" in value)) return null;
  const support = value.support;
  if (!support || typeof support !== "object" || !("email" in support)) return null;
  const email = typeof support.email === "string" ? support.email.trim() : "";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

function publicUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return (
      (url.protocol === "https:" ||
        (url.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname))) &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash
    );
  } catch {
    return false;
  }
}

export function parseAuthDiscovery(value: unknown): AuthDiscovery {
  if (!value || typeof value !== "object" || !("auth" in value)) {
    throw new Error("Invalid authentication configuration.");
  }
  const auth = value.auth;
  if (
    !auth ||
    typeof auth !== "object" ||
    !("provider_url" in auth) ||
    !publicUrl(auth.provider_url) ||
    !("client_id" in auth) ||
    typeof auth.client_id !== "string" ||
    !auth.client_id.trim() ||
    !("scope" in auth) ||
    typeof auth.scope !== "string" ||
    !auth.scope.trim() ||
    !("provider" in auth) ||
    !["oidc", "entra", "cognito"].includes(String(auth.provider)) ||
    !("logout_url" in auth) ||
    (auth.logout_url !== null && !publicUrl(auth.logout_url)) ||
    (auth.provider === "cognito" && !publicUrl(auth.logout_url))
  ) {
    throw new Error("Invalid authentication configuration.");
  }
  return {
    provider_url: auth.provider_url,
    client_id: auth.client_id,
    scope: auth.scope,
    provider: auth.provider as AuthDiscovery["provider"],
    logout_url: auth.logout_url as string | null,
  };
}

export function createOidcConfig(auth: AuthDiscovery, origin: string): AuthProviderProps {
  return {
    authority: auth.provider_url,
    client_id: auth.client_id,
    redirect_uri: `${origin}/postlogin`,
    post_logout_redirect_uri: `${origin}/login`,
    response_type: "code",
    scope: auth.scope,
    automaticSilentRenew: true,
    ...(auth.provider === "cognito" && auth.logout_url
      ? { metadataSeed: { end_session_endpoint: auth.logout_url } }
      : {}),
    userStore: {
      set: (key: string, value: string) => Promise.resolve(localStorage.setItem(key, value)),
      get: (key: string) => Promise.resolve(localStorage.getItem(key)),
      remove: (key: string) => {
        const value = localStorage.getItem(key);
        localStorage.removeItem(key);
        return Promise.resolve(value);
      },
      getAllKeys: () => Promise.resolve(Object.keys(localStorage)),
    },
  };
}

class AuthDiscoveryHttpError extends Error {
  constructor(readonly status: number) {
    super("Authentication service unavailable.");
  }
}

export const publicConfigQueryOptions = queryOptions({
  queryKey: ["public-auth-discovery"],
  queryFn: ({ signal }) => loadPublicConfig(signal),
  staleTime: 60_000,
  retry: (failureCount, error) =>
    failureCount < 3 &&
    (error instanceof TypeError ||
      (error instanceof DOMException && error.name === "TimeoutError") ||
      (error instanceof AuthDiscoveryHttpError &&
        (error.status === 408 || error.status === 429 || error.status >= 500))),
  retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 4_000),
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
});

export async function loadPublicConfig(
  signal: AbortSignal,
): Promise<{ oidc: AuthProviderProps; supportEmail: string | null }> {
  const base = (import.meta.env.VITE_API_ENDPOINT as string | undefined) || window.location.origin;
  const response = await fetch(`${base.replace(/\/$/, "")}/api/info`, {
    signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]),
    cache: "no-store",
    credentials: "omit",
  });
  if (!response.ok) throw new AuthDiscoveryHttpError(response.status);
  const payload: unknown = await response.json();
  return {
    oidc: createOidcConfig(parseAuthDiscovery(payload), window.location.origin),
    supportEmail: parseSupportEmail(payload),
  };
}

export function createSignoutArgs(
  settings: Pick<AuthContextProps["settings"], "client_id" | "metadataSeed">,
  origin: string,
): Parameters<AuthContextProps["signoutRedirect"]>[0] {
  const callback = `${origin}/login`;
  return settings.metadataSeed?.end_session_endpoint
    ? {
        post_logout_redirect_uri: "",
        extraQueryParams: { client_id: settings.client_id, logout_uri: callback },
      }
    : { post_logout_redirect_uri: callback };
}
